import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { Store, select } from '@ngrx/store';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { selectZefDialogState, zefDialogOpen, zefDialogClose, ZefDialogModule } from '@zerops/zef/dialog';
import { ZefScrollbarComponent } from '@zerops/zef/scrollbar';
import { FormSectionModule } from '@zerops/zui/form-section';
import { MarkdownModule } from 'ngx-markdown';
import { environment } from '@zerops/zerops/env';
import { Subject, timer, of, combineLatest } from 'rxjs';
import { switchMap, map, filter, catchError, distinctUntilChanged, shareReplay, startWith, withLatestFrom, tap } from 'rxjs/operators';
import { FEATURE_NAME } from './version-notification.constant';

@Component({
  selector: 'z-version-notification',
  templateUrl: './version-notification.feature.html',
  styleUrl: './version-notification.feature.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    ZefDialogModule,
    ZefScrollbarComponent,
    FormSectionModule,
    MarkdownModule
  ]
})
export class VersionNotificationFeature extends ZefReactiveComponent {
  // # Deps
  #http = inject(HttpClient);
  #store = inject(Store);

  // # Event Streams
  onDismissBar$ = new Subject<void>();
  onCloseDialog$ = new Subject<void>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  #localStorageKey = 'zerops-version-notification-dismissed';

  // -- async
  #barDismissed$ = this.onDismissBar$.pipe(
    map(() => true),
    startWith(false)
  );

  // Single timer source for all polling (60 seconds) - fixes duplicate polling issue
  #pollTrigger$ = timer(0, 1000 * 60).pipe(shareReplay(1));

  version$ = this.#pollTrigger$.pipe(
    switchMap(() => this.#http.get<{ version: string }>(`${environment.versionInfoPath}?t=${Date.now()}`).pipe(
      catchError(() => of(undefined))
    )),
    filter((d) => !!d),
    map((d) => d.version),
    shareReplay(1)
  );

  versionMismatch$ = this.version$.pipe(
    // 0.0.0 is the checked-in placeholder used by direct development builds.
    map((version) => version !== '0.0.0' && version !== environment.appVersion),
    distinctUntilChanged()
  );

  markdownContent$ = this.version$.pipe(
    switchMap((version) =>
      this.#http.get(`/assets/release-notes/${version}.md`, { responseType: 'text' }).pipe(
        map((content) => this.#isValidMarkdown(content) ? content : null),
        catchError(() => of(null))
      )
    ),
    shareReplay(1)
  );

  hasMarkdown$ = this.markdownContent$.pipe(
    map((content) => !!content),
    distinctUntilChanged()
  );

  dialogDismissed$ = this.version$.pipe(
    map((version) => this.#isDialogDismissed(version)),
    distinctUntilChanged()
  );

  displayMode$ = combineLatest([
    this.versionMismatch$,
    this.hasMarkdown$,
    this.dialogDismissed$
  ]).pipe(
    map(([ mismatch, hasMarkdown, dialogDismissed ]) => {
      if (!mismatch) return 'none';
      if (hasMarkdown && dialogDismissed) return 'none';
      return hasMarkdown ? 'dialog' : 'bar';
    }),
    distinctUntilChanged()
  );

  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  dialogOpen$ = this.dialogState$.pipe(map((data) => data?.state ?? false));

  showBar$ = combineLatest([
    this.displayMode$,
    this.dialogOpen$,
    this.#barDismissed$
  ]).pipe(
    map(([ mode, dialogOpen, barDismissed ]) => mode === 'bar' && !dialogOpen && !barDismissed)
  );

  // # State resolver
  state = this.$connect({
    version: this.version$,
    showBar: this.showBar$,
    dialogOpen: this.dialogOpen$,
    markdownContent: this.markdownContent$
  });

  constructor() {
    super();

    // # Dispatcher
    this.$dispatchActions([
      // Auto-open dialog when markdown exists
      this.displayMode$.pipe(
        filter((mode) => mode === 'dialog'),
        map(() => zefDialogOpen({ key: FEATURE_NAME }))
      ),

      // Close dialog and mark as dismissed
      this.onCloseDialog$.pipe(
        withLatestFrom(this.version$),
        tap(([ , version ]) => this.#markDialogAsDismissed(version)),
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      )
    ]);
  }

  #isDialogDismissed(version: string) {
    let raw: string | null;

    try {
      raw = localStorage.getItem(this.#localStorageKey);
    } catch {
      // localStorage is disabled or blocked
      return false;
    }

    if (!raw) return false;

    try {
      // Handle legacy string format (e.g., "v4.3.0" instead of ["v4.3.0"])
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.includes(version);
      }
      // Legacy: single string was stored
      return parsed === version;
    } catch {
      // JSON.parse failed - raw value might be unquoted string like v4.3.0
      return raw === version;
    }
  }

  #markDialogAsDismissed(version: string) {
    try {
      const raw = localStorage.getItem(this.#localStorageKey);
      let dismissedArray: string[] = [];

      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            dismissedArray = parsed;
          } else if (typeof parsed === 'string') {
            // Legacy: single JSON string was stored
            dismissedArray = [ parsed ];
          }
        } catch {
          // JSON.parse failed - raw value is unquoted string like v4.3.0
          dismissedArray = [ raw ];
        }
      }

      if (!dismissedArray.includes(version)) {
        dismissedArray.push(version);
      }
      localStorage.setItem(this.#localStorageKey, JSON.stringify(dismissedArray));
    } catch {
      // localStorage might be disabled or full
    }
  }

  #isValidMarkdown(content: string) {
    // Server returns HTML (the Angular app) for non-existent files instead of 404
    // Check if response looks like HTML rather than markdown
    const trimmed = content?.trim() ?? '';
    return trimmed.length > 0 && !trimmed.startsWith('<!DOCTYPE') && !trimmed.startsWith('<html');
  }
}
